export const MAX_CONCURRENT_IMAGE_UPLOADS = 3;
const START_WINDOW_MS = 1000;

let runningCount = 0;
const waitingForSlot: Array<() => void> = [];
const recentStarts: number[] = [];
const inFlightByDedupeKey = new Map<string, Promise<unknown>>();

const acquireSlot = async (): Promise<void> => {
  if (runningCount < MAX_CONCURRENT_IMAGE_UPLOADS) {
    runningCount += 1;
    return;
  }
  await new Promise<void>((resolve) => {
    waitingForSlot.push(resolve);
  });
};

const releaseSlot = (): void => {
  const nextWaiting = waitingForSlot.shift();
  if (nextWaiting) {
    nextWaiting();
    return;
  }
  runningCount -= 1;
};

const waitForStartWindow = async (): Promise<void> => {
  while (recentStarts.length >= MAX_CONCURRENT_IMAGE_UPLOADS) {
    const waitMs = START_WINDOW_MS - (Date.now() - recentStarts[0]!);
    if (waitMs <= 0) {
      recentStarts.shift();
      continue;
    }
    await new Promise<void>((resolve) => {
      setTimeout(resolve, waitMs);
    });
  }
  recentStarts.push(Date.now());
};

const runWhenSlotIsFree = async <T>(upload: () => Promise<T>): Promise<T> => {
  await acquireSlot();
  try {
    await waitForStartWindow();
    return await upload();
  } finally {
    releaseSlot();
  }
};

interface ImageUploadDedupeKeyParams {
  file: string;
  uploadPreset: string;
}

export const buildImageUploadDedupeKey = ({ file, uploadPreset }: ImageUploadDedupeKeyParams): string =>
  `${uploadPreset}:${file}`;

interface EnqueueImageUploadParams<T> {
  dedupeKey?: string | null;
  upload: () => Promise<T>;
}

export const enqueueImageUpload = <T>({ dedupeKey, upload }: EnqueueImageUploadParams<T>): Promise<T> => {
  if (!dedupeKey) {
    return runWhenSlotIsFree(upload);
  }

  const alreadyInFlight = inFlightByDedupeKey.get(dedupeKey) as Promise<T> | undefined;
  if (alreadyInFlight) {
    return alreadyInFlight;
  }

  const uploadPromise = runWhenSlotIsFree(upload).finally(() => {
    inFlightByDedupeKey.delete(dedupeKey);
  });
  inFlightByDedupeKey.set(dedupeKey, uploadPromise);

  return uploadPromise;
};

export const resetImageUploadQueueForTests = (): void => {
  runningCount = 0;
  waitingForSlot.length = 0;
  recentStarts.length = 0;
  inFlightByDedupeKey.clear();
};
